import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DollarSign, MapPin, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface VenueSection {
  id: string;
  venue_id: string;
  field_id?: string;
  section_name: string;
  section_type: string;
  capacity?: number;
  hourly_rate?: number;
  amenities?: string[];
  is_active: boolean;
}

interface VenueField {
  id: string;
  field_name: string;
  field_type: string;
}

interface VenueSectionsPricingProps {
  venueId: string;
}

const VenueSectionsPricing: React.FC<VenueSectionsPricingProps> = ({ venueId }) => {
  const [sections, setSections] = useState<VenueSection[]>([]);
  const [fields, setFields] = useState<VenueField[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSections();
    loadFields();
  }, [venueId]);

  const loadSections = async () => {
    try {
      const { data, error } = await supabase
        .from('venue_sections')
        .select('*')
        .eq('venue_id', venueId)
        .eq('is_active', true)
        .order('hourly_rate');

      if (error) throw error;
      setSections((data || []).map((section: any) => ({
        ...section,
        amenities: Array.isArray(section.amenities) ? section.amenities.map((item: any) => 
          typeof item === 'string' ? item : String(item)
        ) : []
      })));
    } catch (error) {
      console.error('Error loading sections for pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFields = async () => {
    try {
      const { data, error } = await supabase
        .from('venue_fields')
        .select('*')
        .eq('venue_id', venueId)
        .eq('is_active', true);

      if (error) throw error;
      setFields((data || []).map(field => ({
        ...field,
        field_type: field.sport_type || 'general'
      })));
    } catch (error) {
      console.error('Error loading fields:', error);
    }
  };

  const getFieldName = (fieldId?: string) => {
    if (!fieldId) return 'General';
    const field = fields.find(f => f.id === fieldId);
    return field?.field_name || 'Unknown Field';
  };

  if (loading) {
    return (
      <div className="text-center text-muted-foreground py-4">
        Loading section pricing...
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="text-center py-8">
        <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No section-specific pricing available</p>
        <p className="text-sm text-muted-foreground mt-2">Contact venue for detailed rates</p>
      </div>
    );
  }

  const sectionsWithInfo = sections.filter(section => 
    section.hourly_rate || section.capacity || section.amenities?.length > 0
  );

  if (sectionsWithInfo.length === 0) {
    return (
      <div className="text-center py-8">
        <DollarSign className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">Section information not available</p>
        <p className="text-sm text-muted-foreground mt-2">Contact venue for details</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Section-Specific Pricing</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sectionsWithInfo.map((section) => (
          <Card key={section.id} className="border-border bg-card">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-foreground">{section.section_name}</h4>
                  <p className="text-sm text-muted-foreground">{getFieldName(section.field_id)}</p>
                </div>
                <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                  {section.section_type}
                </Badge>
              </div>

              <div className="space-y-3">
                {/* Hourly Rate */}
                {section.hourly_rate && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-primary">
                      <DollarSign className="h-5 w-5" />
                      <span className="text-2xl font-bold">${section.hourly_rate}</span>
                    </div>
                    <span className="text-muted-foreground text-sm">per hour</span>
                  </div>
                )}

                {/* Deposit Information */}
                {(section as any).deposit_amount && (
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-primary mb-1">
                      <DollarSign className="h-4 w-4" />
                      <span className="font-semibold">Deposit Required: ${(section as any).deposit_amount}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Required for booking this section</p>
                  </div>
                )}

                {/* Capacity Information */}
                {(section.capacity || (section as any).seating_capacity || (section as any).standing_capacity) && (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Users className="h-4 w-4" />
                    <div className="flex flex-col">
                      {section.capacity && <span>Total Capacity: {section.capacity} people</span>}
                      {(section as any).seating_capacity && <span>Seating: {(section as any).seating_capacity}</span>}
                      {(section as any).standing_capacity && <span>Standing: {(section as any).standing_capacity}</span>}
                    </div>
                  </div>
                )}

                {/* Section Details */}
                {((section as any).room_type || (section as any).environment_type || (section as any).age_group) && (
                  <div className="space-y-1">
                    {(section as any).room_type && (
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium">Room Type:</span> {(section as any).room_type}
                      </div>
                    )}
                    {(section as any).environment_type && (
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium">Environment:</span> {(section as any).environment_type}
                      </div>
                    )}
                    {(section as any).age_group && (
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium">Age Group:</span> {(section as any).age_group}
                      </div>
                    )}
                  </div>
                )}

                {/* Amenities */}
                {section.amenities && section.amenities.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-foreground mb-2">Included amenities:</p>
                    <div className="flex flex-wrap gap-1">
                      {section.amenities.slice(0, 3).map((amenity, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="text-xs bg-muted text-muted-foreground border-border"
                        >
                          {amenity}
                        </Badge>
                      ))}
                      {section.amenities.length > 3 && (
                        <Badge
                          variant="outline"
                          className="text-xs bg-muted text-muted-foreground border-border"
                        >
                          +{section.amenities.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default VenueSectionsPricing;